/**
 * Minimal .xlsx (SpreadsheetML) writer.
 *
 * An .xlsx file is a ZIP of XML parts, so this builds the handful of parts
 * Excel/Sheets/Numbers require and packs them with a store-only (method 0)
 * ZIP writer. It is deliberately dependency-free: SheetJS's community build
 * cannot write cell fills (styling is a paid feature) and ExcelJS's browser
 * bundle expects the Node `buffer`/`stream` shims that CRA 5 no longer
 * injects, so either one would have cost a build-config change to get a
 * coloured row out. Everything here is ~200 lines of string building and one
 * CRC table.
 *
 * Only what the export screen needs is supported: one or more sheets,
 * inline strings, numbers, a frozen header, an autofilter, and the fixed
 * style set below.
 */

/* ------------------------------------------------------------------ *
 * ZIP container
 * ------------------------------------------------------------------ */

// Standard CRC-32 (IEEE 802.3) table, built once at module load.
const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let value = i;
    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xEDB88320 ^ (value >>> 1) : value >>> 1;
    }
    table[i] = value >>> 0;
  }
  return table;
})();

/**
 * CRC-32 of a byte array, as required in every ZIP entry header.
 *
 * @param {Uint8Array} bytes
 * @returns {number} unsigned 32-bit checksum
 */
const crc32 = (bytes) => {
  let crc = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = CRC_TABLE[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8);
  }
  return (crc ^ 0xFFFFFFFF) >>> 0;
};

// A fixed DOS timestamp (1980-01-01 00:00) for every entry. The modified
// time of a generated part carries no information, and pinning it keeps two
// exports of the same range byte-identical, which makes them diffable.
const DOS_DATE = 33; // (1980-1980) << 9 | 1 << 5 | 1
const DOS_TIME = 0;

/**
 * Packs named text parts into a ZIP archive with no compression.
 *
 * Store-only keeps this synchronous and avoids CompressionStream, which
 * Safari only gained recently. A booking report is XML in the low megabytes
 * at worst, which is a fine download.
 *
 * @param {Array<{name: string, text: string}>} parts
 * @returns {Blob}
 */
const zipStore = (parts) => {
  const encoder = new TextEncoder();
  const chunks = [];
  const directory = [];
  let offset = 0;

  parts.forEach(({ name, text }) => {
    const nameBytes = encoder.encode(name);
    const data = encoder.encode(text);
    const crc = crc32(data);

    const header = new DataView(new ArrayBuffer(30));
    header.setUint32(0, 0x04034b50, true); // local file header signature
    header.setUint16(4, 20, true); // version needed
    header.setUint16(6, 0x0800, true); // flags: UTF-8 file names
    header.setUint16(8, 0, true); // method: stored
    header.setUint16(10, DOS_TIME, true);
    header.setUint16(12, DOS_DATE, true);
    header.setUint32(14, crc, true);
    header.setUint32(18, data.length, true); // compressed size
    header.setUint32(22, data.length, true); // uncompressed size
    header.setUint16(26, nameBytes.length, true);
    header.setUint16(28, 0, true); // extra field length

    chunks.push(new Uint8Array(header.buffer), nameBytes, data);

    const entry = new DataView(new ArrayBuffer(46));
    entry.setUint32(0, 0x02014b50, true); // central directory signature
    entry.setUint16(4, 20, true); // version made by
    entry.setUint16(6, 20, true); // version needed
    entry.setUint16(8, 0x0800, true);
    entry.setUint16(10, 0, true);
    entry.setUint16(12, DOS_TIME, true);
    entry.setUint16(14, DOS_DATE, true);
    entry.setUint32(16, crc, true);
    entry.setUint32(20, data.length, true);
    entry.setUint32(24, data.length, true);
    entry.setUint16(28, nameBytes.length, true);
    entry.setUint16(30, 0, true); // extra
    entry.setUint16(32, 0, true); // comment
    entry.setUint16(34, 0, true); // disk number start
    entry.setUint16(36, 0, true); // internal attributes
    entry.setUint32(38, 0, true); // external attributes
    entry.setUint32(42, offset, true); // offset of local header
    directory.push(new Uint8Array(entry.buffer), nameBytes);

    offset += 30 + nameBytes.length + data.length;
  });

  const directorySize = directory.reduce((sum, part) => sum + part.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true); // end of central directory signature
  end.setUint16(4, 0, true); // this disk
  end.setUint16(6, 0, true); // disk with central directory
  end.setUint16(8, parts.length, true);
  end.setUint16(10, parts.length, true);
  end.setUint32(12, directorySize, true);
  end.setUint32(16, offset, true);
  end.setUint16(20, 0, true); // comment length

  return new Blob([...chunks, ...directory, new Uint8Array(end.buffer)], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

/* ------------------------------------------------------------------ *
 * XML helpers
 * ------------------------------------------------------------------ */

/**
 * Escapes a value for XML text content.
 *
 * Control characters are stripped rather than escaped: XML 1.0 forbids them
 * outright, and Excel refuses to open a workbook that contains one — a stray
 * vertical tab pasted into a guest name would otherwise produce a file
 * that only fails at the moment someone tries to read the report.
 *
 * @param {*} value
 * @returns {string}
 */
const xmlText = (value) => String(value ?? '')
  // eslint-disable-next-line no-control-regex
  .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

/**
 * Spreadsheet column name for a 1-based index (1 -> A, 27 -> AA).
 *
 * @param {number} index
 * @returns {string}
 */
const columnName = (index) => {
  let name = '';
  let remaining = index;
  while (remaining > 0) {
    const rest = (remaining - 1) % 26;
    name = String.fromCharCode(65 + rest) + name;
    remaining = Math.floor((remaining - 1) / 26);
  }
  return name;
};

/* ------------------------------------------------------------------ *
 * Styles
 * ------------------------------------------------------------------ */

/**
 * Row variants a caller can ask for. The style table below is written to
 * match, so a variant maps to one text style id and one money style id.
 */
const ROW_VARIANTS = ['default', 'header', 'confirmed', 'footer'];

// cellXfs indexes, in the order they are declared in STYLES_XML.
const STYLE_IDS = {
  default: { text: 0, money: 1 },
  header: { text: 2, money: 2 },
  confirmed: { text: 3, money: 4 },
  footer: { text: 5, money: 6 },
};

// numFmtId 164 is the first id available for custom formats (0-163 are
// reserved by the spec). Two decimals with a thousands separator, which is
// what the rate columns are meant to read as.
const STYLES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="1"><numFmt numFmtId="164" formatCode="#,##0.00"/></numFmts>
<fonts count="2">
<font><sz val="11"/><name val="Calibri"/></font>
<font><b/><sz val="11"/><name val="Calibri"/></font>
</fonts>
<fills count="4">
<fill><patternFill patternType="none"/></fill>
<fill><patternFill patternType="gray125"/></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FFD9EAD3"/><bgColor indexed="64"/></patternFill></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FFF1EFE9"/><bgColor indexed="64"/></patternFill></fill>
</fills>
<borders count="3">
<border><left/><right/><top/><bottom/><diagonal/></border>
<border><left/><right/><top/><bottom style="thin"><color rgb="FFBFB8AC"/></bottom><diagonal/></border>
<border><left/><right/><top style="thin"><color rgb="FFBFB8AC"/></top><bottom/><diagonal/></border>
</borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="7">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="0" fontId="1" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/>
<xf numFmtId="0" fontId="0" fillId="2" borderId="0" xfId="0" applyFill="1"/>
<xf numFmtId="164" fontId="0" fillId="2" borderId="0" xfId="0" applyNumberFormat="1" applyFill="1"/>
<xf numFmtId="0" fontId="1" fillId="0" borderId="2" xfId="0" applyFont="1" applyBorder="1"/>
<xf numFmtId="164" fontId="1" fillId="0" borderId="2" xfId="0" applyNumberFormat="1" applyFont="1" applyBorder="1"/>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
<dxfs count="0"/>
</styleSheet>`;

/* ------------------------------------------------------------------ *
 * Workbook
 * ------------------------------------------------------------------ */

/**
 * @typedef {object} XlsxCell
 * @property {string|number|null|undefined} value Blank cells still take the
 *   row's fill, which is what makes a highlighted row read as one band.
 * @property {'text'|'number'|'money'} [type] Defaults to 'text'. 'number'
 *   writes a plain number (a count), 'money' a number with two decimals.
 */

/**
 * @typedef {object} XlsxRow
 * @property {XlsxCell[]} cells
 * @property {'default'|'header'|'confirmed'|'footer'} [variant]
 */

/**
 * Renders one row of cells as <row> XML.
 *
 * @param {XlsxRow} row
 * @param {number} rowNumber 1-based
 * @returns {string}
 */
const rowXml = (row, rowNumber) => {
  const variant = ROW_VARIANTS.includes(row.variant) ? row.variant : 'default';
  const styles = STYLE_IDS[variant];

  const cells = row.cells.map((cell, index) => {
    const reference = `${columnName(index + 1)}${rowNumber}`;
    const isMoney = cell?.type === 'money';
    const isNumber = isMoney || cell?.type === 'number';
    const styleId = isMoney ? styles.money : styles.text;
    const value = cell?.value;

    // A number written as a number, so the sheet can sum and re-format it —
    // a head count stored as text is one Excel silently refuses to add up.
    // Numeric cells fall back to blank rather than 0 when empty, so a column
    // a booking never touched stays visually empty instead of reading 0.00.
    if (isNumber) {
      const numeric = Number(value);
      if (value === null || value === undefined || value === '' || Number.isNaN(numeric)) {
        return `<c r="${reference}" s="${styleId}"/>`;
      }
      return `<c r="${reference}" s="${styleId}"><v>${numeric}</v></c>`;
    }

    if (value === null || value === undefined || value === '') {
      return `<c r="${reference}" s="${styleId}"/>`;
    }

    // Inline strings rather than a shared-strings table: the export is
    // mostly distinct values (names, emails, slot ranges), so the table
    // would add a part and an indirection without saving much.
    return `<c r="${reference}" s="${styleId}" t="inlineStr"><is><t xml:space="preserve">${xmlText(value)}</t></is></c>`;
  });

  return `<row r="${rowNumber}">${cells.join('')}</row>`;
};

/**
 * @typedef {object} XlsxSheet
 * @property {string} name Tab label.
 * @property {XlsxRow[]} rows Row 1 is expected to be the header.
 * @property {number[]} [columnWidths] Widths in character units, in column
 *   order; missing entries fall back to the sheet default.
 * @property {boolean} [autoFilter] Add a filter over the header row.
 */

/**
 * Renders one worksheet part.
 *
 * @param {XlsxSheet} sheet
 * @returns {string} the worksheet XML
 */
const sheetXml = ({ rows = [], columnWidths = [], autoFilter = true }) => {
  const columnCount = rows.reduce((max, row) => Math.max(max, row.cells.length), 0);

  const cols = columnWidths.length > 0
    ? `<cols>${columnWidths
      .map((width, index) => `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`)
      .join('')}</cols>`
    : '';

  const sheetData = rows.map((row, index) => rowXml(row, index + 1)).join('');

  // The filter covers the header and the data rows only. Including the
  // totals row would let a filter hide it, or worse, sort it into the middle
  // of the data.
  const lastDataRow = rows.length > 0 && rows[rows.length - 1].variant === 'footer'
    ? rows.length - 1
    : rows.length;
  const filter = autoFilter && columnCount > 0 && lastDataRow > 1
    ? `<autoFilter ref="A1:${columnName(columnCount)}${lastDataRow}"/>`
    : '';

  // Freezing row 1 matters more here than usual: the sheets are wide enough
  // that a reader scrolling to the rate columns has long since lost track of
  // which booking a row belongs to.
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
${cols}<sheetData>${sheetData}</sheetData>${filter}
</worksheet>`;
};

/**
 * Makes a sheet name Excel will accept: no : \ / ? * [ ] characters, 31
 * characters at most, and unique within the workbook (Excel refuses to open
 * a file with two tabs of the same name, so a duplicate is suffixed rather
 * than left to break the whole download).
 *
 * @param {string} name
 * @param {Set<string>} taken names already used, mutated with the result
 * @returns {string}
 */
const safeSheetName = (name, taken) => {
  const base = String(name || '').replace(/[:\\/?*[\]]/g, ' ').trim().slice(0, 31) || 'Sheet';
  let candidate = base;
  let suffix = 2;
  while (taken.has(candidate.toLowerCase())) {
    const tail = ` (${suffix})`;
    candidate = `${base.slice(0, 31 - tail.length)}${tail}`;
    suffix += 1;
  }
  taken.add(candidate.toLowerCase());
  return candidate;
};

/**
 * Builds an .xlsx workbook with one or more worksheets.
 *
 * @param {object} options
 * @param {XlsxSheet[]} options.sheets In tab order. An empty sheet still
 *   gets a tab — a report with no Open Play in the range should say so with
 *   an empty tab rather than by silently not having one.
 * @returns {Blob} the workbook, ready for a download link
 */
export const buildWorkbookBlob = ({ sheets = [] }) => {
  const list = sheets.length > 0 ? sheets : [{ name: 'Sheet1', rows: [] }];
  const taken = new Set();
  const named = list.map((sheet) => ({ ...sheet, tabName: safeSheetName(sheet.name, taken) }));

  // rId1..rIdN are the worksheets; styles takes the id after the last one.
  const stylesRelId = `rId${named.length + 1}`;

  const parts = [
    {
      name: '[Content_Types].xml',
      text: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
${named.map((_, index) => `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('\n')}
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`,
    },
    {
      name: '_rels/.rels',
      text: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`,
    },
    {
      name: 'xl/workbook.xml',
      text: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets>
${named.map((sheet, index) => `<sheet name="${xmlText(sheet.tabName)}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`).join('\n')}
</sheets>
</workbook>`,
    },
    {
      name: 'xl/_rels/workbook.xml.rels',
      text: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
${named.map((_, index) => `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`).join('\n')}
<Relationship Id="${stylesRelId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`,
    },
    { name: 'xl/styles.xml', text: STYLES_XML },
    ...named.map((sheet, index) => ({
      name: `xl/worksheets/sheet${index + 1}.xml`,
      text: sheetXml(sheet),
    })),
  ];

  return zipStore(parts);
};

/**
 * Triggers a browser download for a generated workbook.
 *
 * @param {Blob} blob
 * @param {string} fileName
 * @returns {void}
 */
export const downloadWorkbook = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
